import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AddTask,
  Edit,
  Delete,
  Save,
  Cancel,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createTickets, updateAgentOutput } from '../../store/strategistSlice';
import { normalizeRichTextHtml } from '../../shared/htmlUtils';
import './BreakdownPreview.scss';

const normalizeTaskTitle = (task, index) => {
  if (typeof task === 'string') return task;
  if (!task || typeof task !== 'object') return `Task ${index + 1}`;
  return task.title || task.task || task.name || `Task ${index + 1}`;
};

const normalizeTaskDescription = (task) => {
  if (typeof task === 'string') return '';
  if (!task || typeof task !== 'object') return '';
  return task.description || task.details || '';
};

const uniqueTaskId = (task, index) =>
  task?.id || task?.task_id || task?.key || `task-${index}-${task?.title || task?.task || task?.name || 'item'}`;

const extractTaskArray = (agentOutput) => {
  if (!agentOutput) return [];
  if (Array.isArray(agentOutput)) return agentOutput;
  if (Array.isArray(agentOutput.tasks)) return agentOutput.tasks;
  if (Array.isArray(agentOutput.generated_tasks)) return agentOutput.generated_tasks;
  return [];
};

const TaskGeneratorPreview = ({
  tasks: agentOutput,
  projectId,
  ticketsGenerated = false,
  agentLogId,
  breakdownHistoryId,
  parentTicketId,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.strategist);
  const isCreating = loading.createTickets;
  const isSaving = loading.updateAgentOutput;

  const rawTasks = useMemo(() => {
    const arr = extractTaskArray(agentOutput);
    return arr.map((task, index) => ({
      id: uniqueTaskId(task, index),
      title: normalizeTaskTitle(task, index),
      description: normalizeTaskDescription(task),
    }));
  }, [agentOutput]);

  const [localTasks, setLocalTasks] = useState([]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedTasks, setEditedTasks] = useState({});
  const [deletedTaskIds, setDeletedTaskIds] = useState(new Set());
  const [isEditingAll, setIsEditingAll] = useState(false);
  const [localTicketsGenerated, setLocalTicketsGenerated] = useState(ticketsGenerated);

  // Sync localTasks when rawTasks changes (on mount / agentOutput prop change)
  React.useEffect(() => {
    setLocalTasks(rawTasks);
  }, [rawTasks]);

  const visibleTasks = useMemo(
    () => localTasks
      .filter((task) => !deletedTaskIds.has(task.id))
      .map((task) => ({
        ...task,
        ...(editedTasks[task.id] || {}),
      })),
    [localTasks, deletedTaskIds, editedTasks]
  );

  const handleTaskFieldChange = (taskId, field, value) => {
    setEditedTasks((prev) => ({
      ...prev,
      [taskId]: { ...(prev[taskId] || {}), [field]: value },
    }));
  };

  const handleStartEdit = (taskId) => {
    if (localTicketsGenerated) return;
    if (!isEditingAll) setEditingTaskId(taskId);
    const task = localTasks.find((item) => item.id === taskId);
    if (!task) return;
    setEditedTasks((prev) => ({
      ...prev,
      [taskId]: { title: task.title || '', description: task.description || '' },
    }));
  };

  const handleCancelEdit = (taskId) => {
    setEditingTaskId(null);
    setEditedTasks((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  const buildAgentOutput = (nextTasks) => {
    const normalizedTasks = nextTasks.map((task) => ({
      title: task.title,
      description: task.description || '',
    }));

    if (agentOutput && typeof agentOutput === 'object' && !Array.isArray(agentOutput)) {
      return {
        ...agentOutput,
        tasks: normalizedTasks,
      };
    }

    return { tasks: normalizedTasks };
  };

  const persistTasks = async (nextTasks) => {
    if (!agentLogId) {
      throw new Error('agent_log_id is required to save task changes');
    }

    await dispatch(updateAgentOutput({
      agentLogId,
      agentOutput: buildAgentOutput(nextTasks),
    })).unwrap();

    if (onRefresh) {
      await onRefresh();
    }
  };

  const handleSaveTask = async (taskId) => {
    const edits = editedTasks[taskId];
    if (!edits) return;

    const nextTasks = localTasks.map((task) => task.id === taskId ? { ...task, ...edits } : task);

    try {
      await persistTasks(nextTasks);
      setLocalTasks(nextTasks);
      setEditingTaskId(null);
      setEditedTasks((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
      toast.success(t('agent.breakdown.changesSaved', 'Changes saved'));
    } catch (err) {
      toast.error(t('agent.breakdown.saveChangesFailed', 'Failed to save changes'));
      console.error('Failed to save task changes:', err);
    }
  };

  const handleEditAll = () => {
    if (localTicketsGenerated) return;
    const next = {};
    localTasks.forEach((task) => {
      if (!deletedTaskIds.has(task.id)) {
        next[task.id] = { title: task.title || '', description: task.description || '' };
      }
    });
    setEditedTasks(next);
    setIsEditingAll(true);
    setEditingTaskId(null);
  };

  const handleDeleteTask = (taskId) => {
    const remaining = localTasks.filter((t) => !deletedTaskIds.has(t.id) && t.id !== taskId);
    if (remaining.length === 0) {
      toast.error(t('agent.taskGenerator.cannotDeleteLastTask', 'You must keep at least one task.'));
      return;
    }
    setDeletedTaskIds((prev) => new Set([...prev, taskId]));
    if (!isEditingAll) setEditingTaskId(taskId);
  };

  const handleDeleteCancel = (taskId) => {
    setDeletedTaskIds((prev) => {
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
    setEditingTaskId(null);
  };

  const handleDeleteSave = async (taskId) => {
    const nextTasks = localTasks.filter((t) => !deletedTaskIds.has(t.id));
    try {
      await persistTasks(nextTasks);
      setLocalTasks(nextTasks);
      setDeletedTaskIds(new Set());
      setEditingTaskId(null);
      toast.success(t('agent.breakdown.changesSaved', 'Changes saved'));
    } catch (err) {
      toast.error(t('agent.breakdown.saveChangesFailed', 'Failed to save changes'));
      console.error('Failed to save delete:', err);
    }
  };

  const handleCancelAll = () => {
    setEditedTasks({});
    setDeletedTaskIds(new Set());
    setIsEditingAll(false);
    setEditingTaskId(null);
  };

  const handleSaveAll = async () => {
    const nextTasks = localTasks
      .map((task) => editedTasks[task.id] ? { ...task, ...editedTasks[task.id] } : task);

    try {
      await persistTasks(nextTasks);
      setLocalTasks(nextTasks);
      setEditedTasks({});
      setIsEditingAll(false);
      setEditingTaskId(null);
      toast.success(t('agent.breakdown.changesSaved', 'Changes saved'));
    } catch (err) {
      toast.error(t('agent.breakdown.saveChangesFailed', 'Failed to save changes'));
      console.error('Failed to save task changes:', err);
    }
  };

  const handleGenerateTickets = async () => {
    if (!projectId || visibleTasks.length === 0 || localTicketsGenerated) return;

    try {
      const payloadTasks = visibleTasks.map((task) => {
        const rawDescription = task.description || '';
        const htmlDescription = rawDescription.replace(/\n/g, '<br>');
        const description = normalizeRichTextHtml(htmlDescription);
        return {
          title: task.title,
          description,
          ...(parentTicketId && { parent_ticket_id: parentTicketId }),
        };
      });

      await dispatch(createTickets({
        projectId,
        tickets: { tasks: payloadTasks },
        breakdownHistoryId: breakdownHistoryId || null,
      })).unwrap();

      setLocalTicketsGenerated(true);
      setIsEditingAll(false);
      setEditingTaskId(null);
      toast.success(t('agent.taskGenerator.ticketsCreated', 'Tickets generated successfully'));

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      toast.error(t('agent.taskGenerator.ticketsCreateFailed', 'Failed to generate tickets'));
      console.error('Failed to generate tickets:', err);
    }
  };

  if (!rawTasks.length) {
    return (
      <Paper elevation={2} className="breakdown-preview-paper">
        <Alert severity="warning">
          {t('agent.taskGenerator.noTasksGenerated', 'No tasks were generated')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Box className="breakdown-preview">
      <Box className="agent-section-header">
        <Box className="agent-section-header-content">
          <Typography variant="h6" className="agent-section-header-title">
            {t('agent.taskGenerator.stepper.taskPreview', 'Task Preview')}
          </Typography>
        </Box>
        <Box className="breakdown-preview-header-right">
          {!localTicketsGenerated && !isEditingAll && (
            <Button startIcon={<Edit />} onClick={handleEditAll} size="small" variant="outlined">
              {t('agent.breakdown.editAll', 'Edit All')}
            </Button>
          )}
          {isEditingAll && (
            <>
              <Button startIcon={<Save />} onClick={handleSaveAll} disabled={isSaving} size="small" variant="contained">
                {isSaving ? t('agent.breakdown.saving', 'Saving...') : t('agent.breakdown.save', 'Save')}
              </Button>
              <Button startIcon={<Cancel />} onClick={handleCancelAll} disabled={isSaving} size="small" variant="outlined">
                {t('agent.breakdown.cancel', 'Cancel')}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Paper elevation={2} className="breakdown-preview-paper">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
          {localTasks.map((task, index) => {
            const isDeleted = deletedTaskIds.has(task.id);
            const isEditing = !isDeleted && (isEditingAll || editingTaskId === task.id);
            const displayTitle = editedTasks[task.id]?.title ?? task.title;
            const displayDescription = editedTasks[task.id]?.description ?? task.description;

            return (
              <Box key={task.id} sx={{ position: 'relative' }}>
              <Paper
                elevation={1}
                className="breakdown-tree-item-paper"
                data-level={0}
                data-type="task"
                sx={{ p: 2, ...(isDeleted && { opacity: 0.6 }) }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        label={`${t('agent.taskGenerator.task', 'Task')} ${index + 1}`}
                        size="small"
                        className="breakdown-tree-item-type-chip breakdown-tree-item-type-chip-task"
                      />
                    </Box>

                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField
                          label={t('agent.taskGenerator.title', 'Title')}
                          value={displayTitle}
                          onChange={(e) => handleTaskFieldChange(task.id, 'title', e.target.value)}
                          size="small"
                          fullWidth
                          className="breakdown-tree-item-title-input"
                        />
                        <TextField
                          label={t('agent.taskGenerator.description', 'Description')}
                          value={displayDescription}
                          onChange={(e) => handleTaskFieldChange(task.id, 'description', e.target.value)}
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          className="breakdown-tree-item-edit-description-field"
                        />
                      </Box>
                    ) : (
                      <>
                        <Typography variant="h6" className="breakdown-tree-item-title" data-level={0}>
                          {displayTitle}
                        </Typography>
                        {displayDescription && (
                          <Typography variant="body2" color="text.secondary" className="breakdown-tree-item-description">
                            {displayDescription}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  {!localTicketsGenerated && (
                    <Box className="breakdown-tree-item-actions">
                      {!isDeleted && !isEditing && (
                        <>
                          <Tooltip title={t('agent.breakdown.edit', 'Edit')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                            <IconButton size="small" onClick={() => handleStartEdit(task.id)} className="breakdown-tree-item-action-button">
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete', 'Delete')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                            <IconButton size="small" onClick={() => handleDeleteTask(task.id)} className="breakdown-tree-item-delete-button">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {isEditing && !isEditingAll && (
                        <Box className="breakdown-tree-item-header-actions">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => handleCancelEdit(task.id)}
                            disabled={isSaving}
                          >
                            {t('agent.breakdown.cancel', 'Cancel')}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Save />}
                            onClick={() => handleSaveTask(task.id)}
                            disabled={isSaving}
                          >
                            {isSaving ? t('agent.breakdown.saving', 'Saving...') : t('agent.breakdown.save', 'Save')}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
              {isDeleted && !isEditingAll && (
                <Box sx={{ position: 'absolute', top: '7px', right: '16px', display: 'flex', gap: 1, zIndex: 10 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Cancel />}
                    onClick={() => handleDeleteCancel(task.id)}
                    disabled={isSaving}
                  >
                    {t('agent.breakdown.cancel', 'Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Delete />}
                    onClick={() => handleDeleteSave(task.id)}
                    disabled={isSaving}
                    color="error"
                  >
                    {isSaving ? t('agent.breakdown.saving', 'Saving...') : t('agent.breakdown.delete', 'Delete')}
                  </Button>
                </Box>
              )}
              </Box>
            );
          })}
        </Box>

        <Box className="breakdown-preview-actions">
          {!localTicketsGenerated && (
            <Button
              variant="contained"
              onClick={handleGenerateTickets}
              disabled={isCreating || visibleTasks.length === 0}
              startIcon={isCreating ? <CircularProgress size={20} /> : <AddTask />}
              className="create-tickets-button"
            >
              {isCreating
                ? t('agent.taskGenerator.generatingTickets', 'Generating Tickets...')
                : t('agent.taskGenerator.generateTickets', 'Generate Tickets')}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default TaskGeneratorPreview;
